import { m } from '@aimfora/i18n'
import { Button } from '@aimfora/ui'
import { useEffect, useState } from 'react'
import { APP_VERSION, VERSION_POLL_MS } from '~/lib/version'

async function serverVersion(): Promise<string | null> {
  try {
    const response = await fetch('/api/version', { cache: 'no-store' })
    if (!response.ok) return null
    const body = (await response.json()) as { version?: unknown }
    return typeof body.version === 'string' ? body.version : null
  } catch {
    return null
  }
}

/**
 * Says so when a new version has been released while the tab was open, with a button to reload.
 * Checks every few minutes and whenever the tab comes back into view; not in development.
 */
export function NewVersionNotice() {
  const [outdated, setOutdated] = useState(false)

  useEffect(() => {
    if (import.meta.env.DEV || APP_VERSION === 'dev') return
    let stopped = false
    const check = async () => {
      if (stopped || document.visibilityState !== 'visible') return
      const version = await serverVersion()
      if (!stopped && version && version !== APP_VERSION) setOutdated(true)
    }
    const timer = window.setInterval(check, VERSION_POLL_MS)
    const onVisible = () => void check()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [])

  if (!outdated) return null
  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-50 flex flex-wrap items-center justify-center gap-3 border-line border-b bg-saffron-soft px-4 py-2 text-sm shadow-sm print:hidden"
    >
      <span>{m.app_new_version()}</span>
      <Button type="button" size="sm" onClick={() => window.location.reload()}>
        {m.app_new_version_reload()}
      </Button>
    </div>
  )
}
